import {
  Children,
  useState,
  useRef,
  useEffect,
  cloneElement,
  isValidElement,
  ReactNode,
  FC,
  useMemo,
  useCallback,
} from 'react';

import styles from './ChartTabs.module.scss';

type TabsProps = {
  value: string | boolean;
  children: ReactNode;
  className?: string;
  onChange?: (value: string) => void;
  fixedWidthTabs: boolean;
};

/**
 * @description Lightweight Tabs like MUI v4 tabs
 * @param value The value of the currently selected `Tab`. If you don't want any selected `Tab`, you can set this property to `false`
 * @param onChange called when tab changes
 * @param fixedWidthTabs if tabs width stay the same based on any state, use cache for better performance
 */

export const ChartTabs: FC<TabsProps> = ({
  value,
  children,
  className = '',
  onChange,
  fixedWidthTabs = false,
}) => {
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const tabsRef = useRef<HTMLDivElement>(null);
  const containerCache = useRef(0);
  const tabsCache = useRef<Array<{ left: number; width: number }>>([]);

  const updateIndicator = useCallback(() => {
    if (!tabsRef.current) return;

    const activeTab = tabsRef.current.querySelector(`[data-value="${value}"]`);
    if (!activeTab) return;

    const tabsLeft =
      fixedWidthTabs && containerCache.current
        ? containerCache.current
        : tabsRef.current.getBoundingClientRect().left;

    const activeTabIndex = activeTab.getAttribute('data-index') || -1;
    const clientRect =
      fixedWidthTabs && tabsCache[activeTabIndex]
        ? tabsCache[activeTabIndex]
        : activeTab.getBoundingClientRect();

    const { left, width } = clientRect;

    if (fixedWidthTabs) {
      containerCache.current = tabsLeft;
      tabsCache[activeTabIndex] = { left, width };
    }

    setIndicator({
      left: left - tabsLeft,
      width,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!tabsRef.current || value === false) return;

    requestAnimationFrame(updateIndicator);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIndicator]);

  const tabs = useMemo(
    () =>
      Children.map(children, (child, index) => {
        if (!isValidElement(child)) return child;

        const isActive = value !== false && child.props.value === value;

        const handleClick = () => {
          if (child.props.onClick) {
            child.props.onClick();
          }
          if (onChange && child.props.value) {
            onChange(child.props.value);
          }
        };

        return cloneElement(child as any, {
          onClick: handleClick,
          active: isActive,
          index,
        });
      }),
    [children, value, onChange]
  );

  return (
    (<div
      className={`${styles.tabsContainer} ${className}`}
      ref={tabsRef}
      data-palette="ChartTabs">
      <div className={styles.tabs}>{tabs}</div>
      <span
        className={styles.indicator}
        style={{
          left: indicator.left,
          width: indicator.width,
          opacity: value !== false ? 1 : 0,
        }}
      />
    </div>)
  );
};
